import { useSearchParams, useLocation } from "react-router-dom";
import { Plus, Book, Loader2, Clipboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCategories, useArticles } from "@/hooks/useKBData";
import { useIntakes } from "@/hooks/useIntakeData";
import { useAuth } from "@/providers/AuthProvider";
import { Link } from "react-router-dom";

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedCategory = searchParams.get("category");

  // Fetch categories and all articles for counts
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useCategories();
  const { data: articlesData, isLoading: articlesLoading } = useArticles();
  const { data: intakesData, isLoading: intakesLoading } = useIntakes({ pageSize: 1 }); // Just for count

  const categories = categoriesData?.categories || [];
  const articles = articlesData?.articles || [];
  const totalArticles = articles.length;
  const totalIntakes = intakesData?.pagination?.totalRecords || 0;

  // Calculate article counts per category
  const getCategoryCount = (categoryId: number) => {
    return articles.filter(article => article.category_id === categoryId).length;
  };

  return (
    <aside className="w-72 border-r border-border bg-background h-[calc(100vh-4rem)] overflow-y-auto p-4">
      {/* Admin: New Article Button */}
      {isAdmin && (
        <Button
          asChild
          className="w-full mb-6 gap-2 rounded-xl h-11 font-semibold"
        >
          <Link to="/app/kb/new">
            <Plus className="h-4 w-4" />
            New Article
          </Link>
        </Button>
      )}

      {/* All Articles */}
      <div className="mb-2">
        <Button
          variant="ghost"
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all h-auto",
            location.pathname === "/app/kb" && !selectedCategory
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
          asChild
        >
          <Link to="/app/kb">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
              <Book className="h-4 w-4" />
            </div>
            <span className="flex-1 text-left">All Articles</span>
            <span className="text-xs text-muted-foreground font-medium">
              {articlesLoading ? "..." : totalArticles}
            </span>
          </Link>
        </Button>
      </div>

      {/* Intake Forms */}
      <div className="mb-2">
        <Button
          variant="ghost"
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all h-auto",
            location.pathname.startsWith("/app/intakes")
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
          asChild
        >
          <Link to="/app/intakes">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
              <Clipboard className="h-4 w-4" />
            </div>
            <span className="flex-1 text-left">Intake Forms</span>
            <span className="text-xs text-muted-foreground font-medium">
              {intakesLoading ? "..." : totalIntakes}
            </span>
          </Link>
        </Button>
      </div>

      {/* Categories Label */}
      <div className="px-3 py-2 mt-4">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Categories
        </span>
      </div>

      {/* Category List */}
      <ScrollArea className="h-[300px]">
        <nav className="space-y-1">
          {categoriesLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : categoriesError ? (
            <div className="text-xs text-muted-foreground p-4 text-center">
              Failed to load categories
            </div>
          ) : categories.length === 0 ? (
            <div className="text-xs text-muted-foreground p-4 text-center">
              No categories found
            </div>
          ) : (
            categories.map((category) => (
              <Button
                key={category.id}
                variant="ghost"
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all h-auto",
                  selectedCategory === category.slug
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
                asChild
              >
                <Link to={`/app/kb?category=${category.slug}`}>
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center",
                    selectedCategory === category.slug ? "bg-primary/20" : "bg-muted"
                  )}>
                    <span className="text-sm">{category.icon}</span>
                  </div>
                  <span className="flex-1 text-left">{category.name}</span>
                  <span className="text-xs text-muted-foreground font-medium">
                    {articlesLoading ? "..." : getCategoryCount(category.id)}
                  </span>
                </Link>
              </Button>
            ))
          )}
        </nav>
      </ScrollArea>

      {/* Help Section */}
      <div className="mt-8 p-4 rounded-xl bg-muted/50 border border-border">
        <h4 className="font-semibold text-sm mb-1">Need help?</h4>
        <p className="text-xs text-muted-foreground mb-3">
          Can't find what you're looking for? Our support team is here to help.
        </p>
        <Button variant="secondary" size="sm" className="w-full rounded-lg text-xs">
          Contact Support
        </Button>
      </div>
    </aside>
  );
}